import { TableCellComponentProps, PrimaryCellValue } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { Avatar } from '@/modules/Avatar';
import { TruncatedWithTooltip } from '@/modules/Tooltip';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  getContentAlignClass,
  isFlexColumn,
} from '../../utils/cellStyles';
import { memo } from 'react';

export const PrimaryCell = memo(
  ({
    value,
    row,
    column,
    state = 'default',
    className,
    onCellFocus,
    stickyLeft = 0,
    isLastLocked = false,
    isRowEditing,
    hasColumnMaxWidthAuto = false,
  }: TableCellComponentProps) => {
    const baseClasses = getTableCellClasses(
      state,
      cn('', className),
      column.locked,
      isLastLocked,
      undefined,
      isRowEditing,
      false,
      column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto)
    );
    const cellProps = getTableCellProps(
      column,
      stickyLeft,
      undefined,
      false,
      hasColumnMaxWidthAuto
    );

    const hasFixedWidth = column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto);
    const alignClass = getContentAlignClass(column.contentAlign);
    const contentClasses = cn(
      'ui-flex ui-items-center ui-gap-8 ui-h-full',
      hasFixedWidth ? 'ui-min-w-0 ui-overflow-hidden' : 'ui-min-w-max',
      alignClass
    );

    const isActive = (state === 'selected' || state === 'focused') && !isRowEditing;
    const textColor = isActive ? 'ui-text-brand-selected' : 'ui-text-primary';
    const subtitleColor = isActive ? 'ui-text-brand-selected' : 'ui-text-primary';

    // Soportar tanto objetos {avatar, name, subtitle} como valores directos
    const isObject = typeof value === 'object' && value !== null && !Array.isArray(value);
    const primaryValue = isObject ? (value as PrimaryCellValue) : null;
    const rowWithAvatar =
      row && typeof row === 'object' && 'avatar' in row
        ? (row as { avatar?: string; subtitle?: string })
        : null;
    const avatar = primaryValue?.avatar ?? rowWithAvatar?.avatar;
    const name = isObject
      ? (value as PrimaryCellValue).name
      : typeof value === 'string' || typeof value === 'number'
        ? value
        : undefined;
    const subtitle = primaryValue?.subtitle ?? rowWithAvatar?.subtitle;

    return (
      <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
        <div className={contentClasses}>
          {avatar && <Avatar type="people" size="sm" src={avatar} />}
          <div
            className={cn(
              'ui-flex ui-min-w-0 ui-flex-col ui-justify-center',
              hasFixedWidth && 'ui-overflow-hidden'
            )}
          >
            <TruncatedWithTooltip
              truncate={hasFixedWidth}
              title={typeof name === 'string' ? name : undefined}
              className={cn(
                hasFixedWidth ? '' : 'ui-whitespace-nowrap',
                'ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm',
                'ui-leading-[20.5px] ui-tracking-[0.5px]',
                textColor
              )}
            >
              {name}
            </TruncatedWithTooltip>
            {subtitle && (
              <TruncatedWithTooltip
                truncate={hasFixedWidth}
                title={typeof subtitle === 'string' ? subtitle : undefined}
                className={cn(
                  hasFixedWidth ? '' : 'ui-whitespace-nowrap',
                  'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
                  'ui-leading-[17.5px] ui-tracking-[0.5px]',
                  subtitleColor
                )}
              >
                {subtitle}
              </TruncatedWithTooltip>
            )}
          </div>
        </div>
      </td>
    );
  }
);

PrimaryCell.displayName = 'PrimaryCell';
